import { cva, type VariantProps } from 'class-variance-authority'
import type { JSX } from 'react'
import { cn } from '#src/cn.ts'

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border border-transparent px-2.5 py-0.5 font-medium text-xs transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        secondary:
          'bg-gray-100 text-gray-900 uppercase tracking-wider dark:bg-gray-800 dark:text-gray-50',
        destructive: 'bg-destructive text-destructive-foreground',
        // Badge contrast redesign (Figma node 527-1754). Geometry and color
        // live in `compoundVariants` so they emit after `size` and `color`.
        outline: 'max-w-56 truncate',
        code: 'h-[18px] rounded bg-muted px-1.5 font-mono font-normal text-[11px] text-muted-foreground leading-none',
      },
      // Two compact geometries that callers used to hand-roll: `sm` is the
      // facesheet header's "AlertPill" (participant flags), `xs` is the
      // condition picker's section tag. They are declared after `variant` so
      // cva emits them later and they win the height/padding/type conflict,
      // and the `outline` compound still overrides them, since cva emits
      // compound variants last.
      size: {
        default: '',
        sm: 'h-6 px-[9px] font-medium text-[10px] leading-4',
        xs: 'h-5 px-1.5 text-2xs',
      },
      color: {
        default: '',
        red: 'border-red-300 bg-red-150 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200',
        orange:
          'border-orange-400/30 bg-yellow-50 text-orange-400 dark:border-orange-700/30 dark:bg-yellow-950/50 dark:text-orange-300',
        yellow:
          'border-yellow-300/50 bg-yellow-100 text-yellow-600 dark:border-yellow-700/50 dark:bg-yellow-950/50 dark:text-yellow-200',
        green:
          'border-green-300 bg-green-100 text-green-700 dark:border-green-900/50 dark:bg-green-950/50 dark:text-green-300',
        blue: 'border-blue-300 bg-blue-100 text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/50 dark:text-blue-300',
        teal: 'border-teal-300 bg-teal-100 text-teal-700 dark:border-teal-900/50 dark:bg-teal-950/50 dark:text-teal-300',
        purple:
          'border-purple-300 bg-purple-100 text-purple-700 dark:border-purple-900/50 dark:bg-purple-950/50 dark:text-purple-300',
        gray: 'border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      color: 'default',
    },
    compoundVariants: [
      {
        variant: 'outline',
        className:
          'h-6 px-[9px] py-1 font-normal text-xs leading-4 tracking-[-0.12px]',
      },
      /* ##BadgeV2Dark The redesign swatches invert under `.dark` on their
       * own, but a color's own `dark:` classes out-specify plain utilities.
       * The `dark:` copies of the swatches replace them in `cn`'s merge. */
      {
        variant: 'outline',
        color: 'default',
        className:
          'border-badge-v2-neutral-border bg-badge-v2-neutral-bg text-badge-v2-neutral-text dark:border-badge-v2-neutral-border dark:bg-badge-v2-neutral-bg dark:text-badge-v2-neutral-text',
      },
      {
        variant: 'outline',
        color: 'red',
        className:
          'border-badge-v2-red-border bg-badge-v2-red-bg text-badge-v2-red-text dark:border-badge-v2-red-border dark:bg-badge-v2-red-bg dark:text-badge-v2-red-text',
      },
      {
        variant: 'outline',
        color: 'orange',
        className:
          'border-badge-v2-orange-border bg-badge-v2-orange-bg text-badge-v2-orange-text dark:border-badge-v2-orange-border dark:bg-badge-v2-orange-bg dark:text-badge-v2-orange-text',
      },
      {
        variant: 'outline',
        color: 'yellow',
        className:
          'border-badge-v2-yellow-border bg-badge-v2-yellow-bg text-badge-v2-yellow-text dark:border-badge-v2-yellow-border dark:bg-badge-v2-yellow-bg dark:text-badge-v2-yellow-text',
      },
      {
        variant: 'outline',
        color: 'green',
        className:
          'border-badge-v2-green-border bg-badge-v2-green-bg text-badge-v2-green-text dark:border-badge-v2-green-border dark:bg-badge-v2-green-bg dark:text-badge-v2-green-text',
      },
      {
        variant: 'outline',
        color: 'blue',
        className:
          'border-badge-v2-blue-border bg-badge-v2-blue-bg text-badge-v2-blue-text dark:border-badge-v2-blue-border dark:bg-badge-v2-blue-bg dark:text-badge-v2-blue-text',
      },
      {
        variant: 'outline',
        color: 'teal',
        className:
          'border-badge-v2-teal-border bg-badge-v2-teal-bg text-badge-v2-teal-text dark:border-badge-v2-teal-border dark:bg-badge-v2-teal-bg dark:text-badge-v2-teal-text',
      },
      {
        variant: 'outline',
        color: 'purple',
        className:
          'border-badge-v2-purple-border bg-badge-v2-purple-bg text-badge-v2-purple-text dark:border-badge-v2-purple-border dark:bg-badge-v2-purple-bg dark:text-badge-v2-purple-text',
      },
      // Figma's "Neutral" swatch covers both `default` and `gray`.
      {
        variant: 'outline',
        color: 'gray',
        className:
          'border-badge-v2-neutral-border bg-badge-v2-neutral-bg text-badge-v2-neutral-text dark:border-badge-v2-neutral-border dark:bg-badge-v2-neutral-bg dark:text-badge-v2-neutral-text',
      },
      // `purple` is the Figma purple swatch. It has no consumer
      // yet — design asked for it to exist "just in case". `cyan` was folded
      // into `teal` (they rendered identically), so `teal` is now the single
      // teal/cyan color.
    ],
  }
)

export interface BadgeProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    VariantProps<typeof badgeVariants> {}

export function Badge({
  className,
  variant,
  size,
  color,
  onClick,
  ...props
}: BadgeProps): JSX.Element {
  return (
    <div
      className={cn(
        badgeVariants({ variant, size, color }),
        onClick &&
          'cursor-pointer hover:brightness-hover dark:hover:brightness-hover',
        className
      )}
      onClick={onClick}
      {...props}
    />
  )
}
